<template>
  <div class="grid-content" ref="campaignsAdGroupsTableContainer">
    <b-table :data="adGroups" hoverable sticky-header v-if="adGroups">
      <b-table-column v-for="column in columns" :key="column.id" :field="column.name" :cell-class="column.class"
        :label="column.label" :visible="isAdGroupColumnVisible(column)" :width="column.width" :sortable="column.sortable"
        :header-class="'column-th-' + column.name">

        <template v-slot="props">
          <div v-if="column.name === 'AdGroupTitle'" :style="getColumnStyle(column)">
            <div class="text active" @click="loadAds(props.row)">{{ props.row[column.name] }}</div>
          </div>
          <div v-else :style="getColumnStyle(column)">
            {{ getFormattedValue(props.row[column.name], column.name) }}
          </div>
        </template>
      </b-table-column>

      <b-table-column cell-class="cell-actions" field="actions" :label="$t('Campaigns.Grid.Columns.Actions')" width="265"
        :sticky="true" v-slot="props" :visible="isActionsColumnVisible">
        <div class="actions-list">
          <b-tooltip :label="$t('Campaigns.AdGroupsGrid.ActionsColumn.GoToFacebook')" position="is-top" type="is-dark"
            v-if="showFacebookAction(selectedCampaign.ChannelMetadataType)">
            <a :href="`https://www.facebook.com/adsmanager/manage/adsets?${props.row.AdCampaignId?.replace('_', '=')}&selected_adset_ids=${props.row.AdGroupId}`"
              class="action action-alerts">
              <icon-base width="8" height="16" view-box="0 0 264 512" icon-name="facebook"><icon-facebook /></icon-base>
            </a>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.AdGroupsGrid.ActionsColumn.GoToAdwords')" position="is-top" type="is-dark"
            v-if="showGoogleAction(selectedCampaign.ChannelMetadataType)">
            <a :href="`https://ads.google.com/aw/overview?campaignId=${props.row.AdCampaignId?.replace('_', '=')}&adGroupId=${props.row.AdGroupId}`"
              class="action action-alerts">
              <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="google"><icon-google /></icon-base>
            </a>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.AdGroupsGrid.ActionsColumn.GoToAdwords')" position="is-top" type="is-dark"
            v-if="showTikTokAction(selectedCampaign.ChannelMetadataType)">
            <a :href="`https://ads.tiktok.com/i18n/perf/campaign`" class="action action-alerts" target="_blank">
              <i class="fa fa-tiktok">
                <img src="/CRM/img/tiktok-icon.svg" />
              </i>
            </a>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.AdGroupsGrid.ActionsColumn.GoToAdwords')" position="is-top" type="is-dark"
            v-if="showOutbrainAction(selectedCampaign.ChannelMetadataType)">
            <a :href="`https://my.outbrain.com`" class="action action-alerts" target="_blank">
              <i class="fa fa-outbrain">
                <img :src="require('@/assets/outbrain.png')" />
              </i>       
            </a>
          </b-tooltip>
        </div>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import { LoaderComponent } from "vue-loading-overlay";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { campaignEditRoute } from "@/router";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignAdGridViewModel } from "@/models/campaigns/CampaignAdGridViewModel";
import { CampaignAdGroupGridViewModel } from "@/models/campaigns/CampaignAdGroupGridViewModel";
import { CampaignGridView, RefreshGridMode } from "@/models/campaigns/CampaignGridView";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { ChannelMetadataType } from "@/models/campaigns/ChannelMetadataType";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsApiService from "@/services/api/CampaignsApiService";
import CampaignsAdGroupsGridHeadline from "./CampaignsAdGroupsGridHeadline.vue";
import IconBase from "../../components/IconBase.vue";
import IconBell from "../../components/icons/IconBell.vue";
import IconChartLine from "../../components/icons/IconChartLine.vue";
import IconChartPie from "../../components/icons/IconChartPie.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconFacebook from "../../components/icons/IconFacebook.vue";
import IconGoogle from "../../components/icons/IconGoogle.vue";
import IconHistory from "../../components/icons/IconHistory.vue";
import IconMobileNo from "../../components/icons/IconMobileNo.vue";
import IconMobileYes from "../../components/icons/IconMobileYes.vue";
import IconPencil from "../../components/icons/IconPencil.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconUpdate from "../../components/icons/IconUpdate.vue";

const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsAdGroupsGridHeadline,
    IconBase,
    IconChartLine,
    IconChartPie,
    IconEye,
    IconFacebook,
    IconGoogle,
    IconPencil,
    IconBell,
    IconHistory,
    IconMobileYes,
    IconMobileNo,
    IconTrash,
    IconUpdate,
  },
})
export default class CampaignsAdGroupsGridContent extends Vue {
  @Prop() readonly adGroups!: CampaignAdGroupGridViewModel[];
  @Prop() readonly columns!: GridColumn[];

  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsLoading", { namespace }) campaignsLoading!: boolean;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("page", { namespace }) page!: number;
  @Getter("selectedCampaign", { namespace }) selectedCampaign!: CampaignGridView;
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Action("setNeedToRefreshCampaignsGrid", { namespace }) setNeedToRefreshCampaignsGrid!: (needToRefreshCampaignsGrid: RefreshGridMode) => void;
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setGraphsHiddenOnScroll", { namespace }) setGraphsHiddenOnScroll!: (graphsHiddenOnScroll: boolean) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedCampaign", { namespace }) setSelectedCampaign!: (selectedCampaign?: CampaignGridView) => void;
  @Action("setSelectedCampaignId", { namespace }) setSelectedCampaignId!: (campaignClientId?: number) => void;
  @Action("setSortDir", { namespace }) setSortDir!: (sortDir: string) => void;
  @Action("setSortField", { namespace }) setSortField!: (sortField: string) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  isColumnFiltersUpdated = false;
  tableDataKey = 0;
  campaignsAdGroupsTableContainer: Element | Window | null = null;
  campaignsTableLoader?: LoaderComponent = undefined;

  CampaignStatus = CampaignStatus;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignsLoading", { immediate: true })
  onCampaignsLoadingChange(newValue: boolean, oldValue: boolean): void {
    if (newValue) {
      this.campaignsTableLoader = this.$loading.show({
        container: this.$refs.campaignsAdGroupsTableContainer
      });
    } else {
      this.campaignsTableLoader?.hide();
      if (this.$el) {
        if (window.innerWidth > 768) {
          this.campaignsAdGroupsTableContainer = this.$el.querySelector(".campaigns-table .table-wrapper");
          if (this.campaignsAdGroupsTableContainer) {
            if (this.page === 1) {
              (this.campaignsAdGroupsTableContainer as Element).scrollTop = 0;
            }
            this.campaignsAdGroupsTableContainer.addEventListener('scroll', this.onScrollTable);
          }
        } else {
          this.campaignsAdGroupsTableContainer = window;
          if (this.page === 1) {
            window.scrollTo({ top: 0 });
          }
          this.campaignsAdGroupsTableContainer.addEventListener('scroll', this.onScrollWindow);
        }
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get campaignEditRouteValue(): string {
    return campaignEditRoute;
  }

  get isActionsColumnVisible(): boolean {
    const column: GridColumn | undefined = this.columns.find((i: GridColumn) => i.name === "Actions");
    if (column) {
      return column.visible;
    } else {
      return false;
    }
  }

  //#endregion Getters


  //#region Handlers

  onPageChange(page: number) {
    this.setPage(page);
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.GridOnly);
  }

  onPageSort(fieldName: string, sortDirection: string, e: PointerEvent) {
    this.setSortDir(sortDirection);
    this.setSortField(fieldName);
    this.setPage(1);
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.GridOnly);
  }

  onScrollWindow(e: Event) {
    const target: Document = e.target as Document;
    if (this.campaigns.Total && target.documentElement.scrollTop >= document.documentElement.offsetHeight - window.innerHeight
      && this.page * CampaignService.getPageSize() < this.campaigns.Total) {
      if (this.campaignsAdGroupsTableContainer) {
        this.campaignsAdGroupsTableContainer.removeEventListener('scroll', this.onScrollWindow);
      };
      this.onPageChange(this.page + 1);
    }
  }

  onScrollTable(e: Event) {
    const target: HTMLDivElement = e.target as HTMLDivElement;
    const EXTRA_PIXELS = 2; // for browser zoom
    const contentHeight: number = target.scrollHeight - target.clientHeight - EXTRA_PIXELS;
    if (this.campaigns.Total && target.scrollTop >= contentHeight && this.page * CampaignService.getPageSize() < this.campaigns.Total) {
      if (this.campaignsAdGroupsTableContainer) {
        this.campaignsAdGroupsTableContainer.removeEventListener('scroll', this.onScrollTable);
      };
      this.onPageChange(this.page + 1);
    }
    this.setGraphsHiddenOnScroll(target.scrollTop > 10 ? true : false);
  }

  //#endregion Handlers


  //#region Private methods

  getColumnStyle(column: GridColumn): string {
    return HelperService.getColumnStyle(column);
  }

  getFormattedValue(value: number | string | Date, fieldName: string): string {
    if (!value) {
      return "";
    }
    if (["Conversion", "CTR"].includes(fieldName)) {
      return `${parseFloat((value as number).toFixed(2)) * 100}%`;
    } else if (["AverageClickCost", "AverageImpressionCost", "AverageLeadCost", "AverageViewCost", "AverageVisitorCost", "MeetingPrice"].includes(fieldName)) {
      return parseFloat((value as number).toFixed(2)).toString();
    } else {
      return value as string;
    }
  }

  isAdGroupColumnVisible(column: GridColumn) {
    const columnsForPricesAllowed: string[] = [
      "AverageClickCost",
      "AverageImpressionCost",
      "AverageLeadCost",
      "AverageViewCost",
      "AverageVisitorCost",
      "Cost",
      "DealAmount",
      "MeetingPrice",
    ];
    return column.visible && !column.forbidden && column.id !== 1 && (
      this.campaignsPageData.PricesAllowed || !this.campaignsPageData.PricesAllowed && !columnsForPricesAllowed.includes(column.name)
    );
  }

  loadAds(adGroup: CampaignAdGroupGridViewModel): void {
    let loader = this.$loading.show({
      container: this.$refs.campaignsAdGroupsTableContainer,
    });
    const dateFrom: string | undefined = this.datePeriod[0] ? moment(this.datePeriod[0]).format("YYYY-MM-DD") : undefined;
    const dateTo: string | undefined = this.datePeriod[0] ? moment(this.datePeriod[1]).format("YYYY-MM-DD") : undefined;
    CampaignsApiService.getCampaignAds(adGroup.ChannelId, adGroup.AverageLeadCost, adGroup.AdGroupId, dateFrom, dateTo)
      .then((result: DataSourceResult) => {
        const ads = result.Data as CampaignAdGridViewModel[];
        this.$emit("ads-loaded", ads, adGroup);
      })
      .finally(() => {
        loader.hide();
      });
  }

  showFacebookAction(metadataType: ChannelMetadataType): boolean {
    return [
      ChannelMetadataType.FacebookAds,
      ChannelMetadataType.InstagramAds,
      ChannelMetadataType.FacebookAdLeadCampaign,
      ChannelMetadataType.InstagramAdLeadCampaign,
    ].indexOf(metadataType) >= 0;
  }

  showGoogleAction(metadataType: ChannelMetadataType): boolean {
    return [
      ChannelMetadataType.GoogleAdwords,
    ].indexOf(metadataType) >= 0;
  }

  showTikTokAction(metadataType: ChannelMetadataType): boolean {
    return [
      ChannelMetadataType.TikTokAds,
      ChannelMetadataType.TikTokLeads
    ].indexOf(metadataType) >= 0;
  }

  showOutbrainAction(metadataType: ChannelMetadataType): boolean {
    return [
      ChannelMetadataType.Outbrain,
    ].indexOf(metadataType) >= 0;
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-content {

  .table thead th,
  .table tbody td {

    &.cell-ad-group-title {
      &>div {
        .text.active {
          color: $primary;
          cursor: pointer;
        }
      }
    }
  }

  .actions-list {
    .action {
      &.action-alerts {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .icon-w {
          line-height: 0;
        }

        .tag.is-small {
          position: absolute;
          left: 50%;
          margin-left: 1px;
          top: -11px;
          height: 19px;
          line-height: 12px;
          border: 2px solid #fff;
          border-radius: 4px;
          padding: 0;
          width: auto;

          .tag-i {
            border: 1px solid $danger;
            background: #fff;
            color: $danger;
            border-radius: 2px;
            padding: 0 2px;
            font-weight: 600;
          }
        }
      }

      &.action-phone {
        .icon-mobile-yes {
          color: $success;
        }

        .icon-mobile-no {
          color: $danger;
        }
      }
    }

    .tooltip-phone {
      .tooltip-content {
        transform: translateX(-20%);

        &:before {
          left: 20%;
        }
      }
    }
  }
}

.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;

  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}

html[dir="ltr"] {
  .grid-content {
    .actions-list {
      .tooltip-phone {
        .tooltip-content {
          transform: translateX(-80%);

          &:before {
            left: 80%;
          }
        }
      }
    }
  }
}
</style>
